import { Badge } from '@mui/material';
import React from 'react';

import { DATA_TEST, getDynamicDataTestId } from '../../enums';
import { BadgeVariant } from '../../types';

interface IBadgeNotificationProps {
  badgeContent?: number;
  badgeVariant?: BadgeVariant;
  children: React.ReactNode;
  top?: string | null;
  right?: string | null;
}

/**
 * Generates an accessible aria-label suffix that includes badge notification information
 * @param badgeContent - The number of notifications
 * @param badgeVariant - The variant of the badge ('dot' or 'standard')
 * @returns A string describing the badge notification, or empty string if no badge
 */
export const getBadgeAccessibilitySuffix = (badgeContent?: number, badgeVariant?: BadgeVariant): string => {
  if (badgeVariant === 'dot') {
    return ', has notifications';
  }

  if (badgeContent !== undefined && badgeContent > 0) {
    const notificationText = badgeContent === 1 ? 'notification' : 'notifications';
    return `, ${badgeContent} ${notificationText}`;
  }

  return '';
};

export const BadgeNotification = ({ badgeContent, children, badgeVariant, top, right }: IBadgeNotificationProps) => {
  const variant = !badgeVariant && (badgeContent ?? 0) > 0 ? 'standard' : badgeVariant;

  return (
    <Badge
      color="primary"
      data-test={getDynamicDataTestId(DATA_TEST.badge_notification, variant)}
      badgeContent={badgeContent === 0 ? undefined : badgeContent}
      variant={variant}
      sx={{ '& .MuiBadge-badge': { top, right } }}
    >
      {children}
    </Badge>
  );
};
